import React from 'react'

export interface CarouselDotsProps {
  /** Number of dots to render. */
  total: number
  /**
   * 1-indexed position of the active dot. The active dot renders as a wider
   * pill with the teal→purple brand gradient; the rest render as smaller
   * neutral pills. Values outside `[1, total]` are clamped silently.
   */
  current: number
  /**
   * Visual treatment for the non-active dots.
   *
   * - `'neutral'` (default): all inactive dots render in the soft muted
   *   `$body-lighter @ 0.45` treatment — used by the Smart Create Playlist
   *   modal where step direction (already-visited vs not-yet-reached) is
   *   not emphasised.
   * - `'phased'`: distinguishes COMPLETED dots (i + 1 < current → solid teal
   *   `$teal-60`) from UPCOMING dots (i + 1 > current → solid
   *   `$purple-brand`). Used by the Vibe Line modal per Figma `3663:104331`
   *   onward, where the user is walking a directed flow and the design
   *   encodes progress visually. Active dot styling is unchanged.
   */
  variant?: 'neutral' | 'phased'
  /** Optional extra class for layout / margin overrides. */
  className?: string
}

/**
 * Pill-style carousel step indicator used at the bottom of the redesigned
 * Smart Create Playlist (3 dots) and Vibe Line (4 dots) modals.
 *
 * Decorative-only — `role="presentation"` so screen readers skip it. The
 * authoritative step state is communicated via the surrounding modal's
 * heading copy ("Pick up to 2", "Pick 1", etc.) and via button labels
 * ("Next", "Create", "Save"), which are reachable.
 */
function CarouselDots ({
  total, current, variant = 'neutral', className = '',
}: CarouselDotsProps) {
  const clamped = Math.min(Math.max(current, 1), total)
  const rootClass = ['carousel-dots', className].filter(Boolean).join(' ')

  return (
    <div className={rootClass} role="presentation" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => {
        const position = i + 1
        const isActive = position === clamped
        // For the `'phased'` variant: tag pre-active pills as `--completed`
        // (rendered teal) and post-active pills as `--upcoming` (purple).
        // The active pill keeps its existing gradient styling regardless of
        // variant, so we never stack `--active` with the phase modifiers.
        let phaseModifier = ''
        if (variant === 'phased' && !isActive) {
          phaseModifier = position < clamped
            ? 'carousel-dots__pill--completed'
            : 'carousel-dots__pill--upcoming'
        }
        const pillClass = [
          'carousel-dots__pill',
          isActive ? 'carousel-dots__pill--active' : '',
          phaseModifier,
        ].filter(Boolean).join(' ')
        return <span key={i} className={pillClass} />
      })}
    </div>
  )
}

// Memoize: all props are primitives (number, number, string), so referential
// equality is content equality. The component is rendered inside the smart
// modal's footer, which re-renders on every keystroke in the seed / interests
// inputs until the parent composition split lands. See plan
// `do-a-code-review-eager-twilight.md` (finding #8).
export default React.memo(CarouselDots)
